import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { Layout } from "./Layout";
import copy from "copy-to-clipboard";
import {
  remliLogin,
  submitChallenge,
  ChallengeAnalysis,
  getActiveApiKey,
  listSessions,
  deleteSession,
  generateNewApiKey,
  getLocationFromIP,
} from "../services/remli";
import { REDIRECT_ROOT } from "../config";

interface CloudProps {
  isAuthenticated: boolean;
  user?: { id: string; email: string };
}

export function Cloud({ isAuthenticated, user }: CloudProps) {
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [currentChallengeAnalysis, setCurrentChallengeAnalysis] = useState<ChallengeAnalysis | null>(null);
  const [challengeCode, setChallengeCode] = useState<string>("");
  const [showChallengeSection, setShowChallengeSection] = useState<boolean>(false);
  const [isRetry, setIsRetry] = useState<boolean>(false);
  const [existingSessionId, setExistingSessionId] = useState<string | null>(null);
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [showApiKeySection, setShowApiKeySection] = useState<boolean>(false);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);
  const [isCheckingSession, setIsCheckingSession] = useState<boolean>(true);
  const [sessionTestResult, setSessionTestResult] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState<boolean>(false);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);

  // Check for existing sessions and API keys on component mount
  useEffect(() => {
    if (isAuthenticated) {
      checkExistingSessionAndApiKey();
    }
  }, [isAuthenticated]);

  // Cleanup polling on component unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const checkExistingSessionAndApiKey = async () => {
    setIsCheckingSession(true);
    try {
      // Step 1: Check for existing REMLI sessions first
      const sessionsResponse = await listSessions();
      let hasValidSession = false;
      let activeSessionId = null;

      if (sessionsResponse.sessions && sessionsResponse.sessions.length > 0) {
        // Use the first session since listSessions only returns active sessions
        const activeSession = sessionsResponse.sessions[0];
        hasValidSession = true;
        activeSessionId = activeSession.session_id;
        setExistingSessionId(activeSession.session_id);
        setUsername(activeSession.username || "");
      }

      // Step 2: If we have a valid session, check for API key
      if (hasValidSession) {
        const existingApiKey = await getActiveApiKey();
        if (existingApiKey) {
          // API key exists, show it
          setApiKey(existingApiKey.api_key);
          setShowApiKeySection(true);
        } else {
          // No API key exists, generate one
          const apiKeyResponse = await generateNewApiKey();
          setApiKey(apiKeyResponse.api_key);
          setShowApiKeySection(true);
        }
      }
      // If no valid session, the user will see the login form
    } catch (error) {
      console.error("Error checking existing session:", error);
    } finally {
      setIsCheckingSession(false);
    }
  };

  const regenerateApiKey = async () => {
    setIsLoading(true);
    try {
      const apiKeyResponse = await generateNewApiKey();
      setApiKey(apiKeyResponse.api_key);
      setCopySuccess(false);
      setMessage("API key regenerated successfully!");
    } catch (error) {
      setError(error instanceof Error ? error.message : "Failed to regenerate API key");
    } finally {
      setIsLoading(false);
    }
  };

  const testSession = async () => {
    if (!existingSessionId) return;

    setIsLoading(true);
    // TODO: Add code to test the session
    try {
      setSessionTestResult("Session test successful!");
      setMessage("Session test successful!");
    } catch (error) {
      setSessionTestResult("Session test failed");
      setError(error instanceof Error ? error.message : "Session test failed");
    } finally {
      setIsLoading(false);
    }
  };

  const checkChallengeStatus = async () => {
    if (!currentSessionId || !currentChallengeAnalysis) return;

    try {
      const response = await submitChallenge({
        session_id: currentSessionId,
        challenge: "", // Empty challenge for app verification
      });

      if (!response.session_id) {
        // Challenge failed or error occurred
        stopPolling();
        setError(response.message || "App verification failed");
        return;
      }

      if (response.challenge_analysis) {
        // Still on challenge page, continue polling
        setCurrentChallengeAnalysis(response.challenge_analysis);
        return;
      } else {
        // Challenge successful - session has progressed
        stopPolling();
        showStatus(`App verification successful! Final Session ID: ${response.session_id}`, "success");
        setShowChallengeSection(false);
        setCurrentSessionId(null);
        setCurrentChallengeAnalysis(null);
        // Generate API key after successful app verification
        const apiKeyResponse = await generateNewApiKey();
        setApiKey(apiKeyResponse.api_key);
        setShowApiKeySection(true);
      }
    } catch (error) {
      // Stop polling on error
      stopPolling();
      setError(error instanceof Error ? error.message : "An unexpected error occurred");
    }
  };

  const startPolling = () => {
    setIsPolling(true);
    const interval = setInterval(checkChallengeStatus, 5000); // Check every 5 seconds
    setPollingInterval(interval);
  };

  const stopPolling = () => {
    setIsPolling(false);
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  };

  const resetSession = async () => {
    if (!existingSessionId) return;

    setIsLoading(true);
    try {
      await deleteSession(existingSessionId);
      setExistingSessionId(null);
      setApiKey(null);
      setShowApiKeySection(false);
      setSessionTestResult(null);
      setMessage("Session reset successfully. You can now start a new login.");
    } catch (error) {
      setError(error instanceof Error ? error.message : "Failed to reset session");
    } finally {
      setIsLoading(false);
    }
  };

  const showStatus = (message: string, type: 'success' | 'error' | 'info') => {
    setMessage(message);
    setError(type === 'error' ? message : null);
  };

  const copyToClipboard = (text: string) => {
    try {
      const success = copy(text);
      if (success) {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } else {
        setError("Failed to copy to clipboard");
      }
    } catch (err) {
      setError("Failed to copy to clipboard");
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setExistingSessionId(null);
    setIsLoading(true);

    if (!username || !password) {
      setError("Please enter both username and password");
      setIsLoading(false);
      return;
    }

    try {
      // Get location data from IP (no permission needed)
      const locationData = await getLocationFromIP();

      const response = await remliLogin({
        username,
        password,
        location: locationData || undefined
      });

      if (!response.session_id) {
        setError(response.message || "Login failed");
        setIsLoading(false);
        return;
      }

      // Check if this is an existing session response
      if (response.message && response.message.includes("already has an active session")) {
        setExistingSessionId(response.session_id);
        showStatus(response.message, "info");
        setIsLoading(false);
        return;
      }

      setCurrentSessionId(response.session_id);

      if (response.challenge_analysis) {
        // Challenge required
        setCurrentChallengeAnalysis(response.challenge_analysis);
        setShowChallengeSection(true);
        setIsRetry(false);

        if (response.challenge_analysis.challenge_type === "app_verification") {
          showStatus("App verification required - checking status automatically...", "info");
          startPolling();
        } else {
          showStatus("Challenge required - please enter the verification code", "info");
        }
      } else {
        // Login successful
        showStatus(`Login successful! Session ID: ${response.session_id}`, "success");
        // Generate API key after successful login
        const apiKeyResponse = await generateNewApiKey();
        setApiKey(apiKeyResponse.api_key);
        setShowApiKeySection(true);
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : "An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChallengeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (!challengeCode) {
      setError("Please enter the challenge code");
      return;
    }

    if (!currentSessionId || !currentChallengeAnalysis) {
      setError("No active session found");
      return;
    }

    setIsLoading(true);

    try {
      const response = await submitChallenge({
        session_id: currentSessionId,
        challenge: challengeCode,
      });

      if (!response.session_id) {
        setError(response.message || "Challenge failed");
        setIsLoading(false);
        return;
      }

      if (response.challenge_analysis) {
        // Another challenge required - could be a retry of the same type
        const isRetryScenario = currentChallengeAnalysis &&
                       currentChallengeAnalysis.challenge_type === response.challenge_analysis.challenge_type;

        if (isRetryScenario) {
          setError("Incorrect code. Please try again.");
          // Clear the input field for retry
          setChallengeCode("");
          setIsRetry(true);
        } else {
          showStatus("Additional challenge required", "info");
          setIsRetry(false);
        }

        // Update the challenge analysis with the new one
        setCurrentChallengeAnalysis(response.challenge_analysis);
      } else {
        // Challenge successful
        showStatus(`Challenge successful! Final Session ID: ${response.session_id}`, "success");
        setShowChallengeSection(false);
        setCurrentSessionId(null);
        setCurrentChallengeAnalysis(null);
        setChallengeCode("");
        // Generate API key after successful challenge completion
        const apiKeyResponse = await generateNewApiKey();
        setApiKey(apiKeyResponse.api_key);
        setShowApiKeySection(true);
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : "An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAppVerification = async () => {
    if (!currentSessionId || !currentChallengeAnalysis) {
      setError("No active session found");
      return;
    }

    // If not already polling, start polling
    if (!isPolling) {
      startPolling();
      showStatus("Started automatic verification checking...", "info");
    } else {
      // If already polling, do a manual check
      setIsLoading(true);
      try {
        await checkChallengeStatus();
      } finally {
        setIsLoading(false);
      }
    }
  };

  const resetForm = () => {
    stopPolling();
    setShowChallengeSection(false);
    setChallengeCode("");
    setCurrentSessionId(null);
    setCurrentChallengeAnalysis(null);
    setIsRetry(false);
    setExistingSessionId(null);
    setError(null);
    setMessage(null);
    setApiKey(null);
    setShowApiKeySection(false);
    setCopySuccess(false);
    setSessionTestResult(null);
  };

  const getChallengeInstructions = (analysis: ChallengeAnalysis, isRetry: boolean = false) => {
    let instructions = "";

    switch (analysis.challenge_type) {
      case "2fa_sms":
        instructions = "SMS Verification Required - Please check your phone for a text message from LinkedIn and enter the verification code below.";
        break;
      case "2fa_email":
        instructions = "Email Verification Required - Please check your email for a verification code from LinkedIn and enter it below.";
        break;
      case "2fa_authenticator":
        instructions = "Authenticator App Required - Please open your authenticator app (Google Authenticator, Microsoft Authenticator, etc.) and enter the 6-digit code for LinkedIn.";
        break;
      case "2fa_generic":
        instructions = "Two-Factor Authentication Required - Please enter the verification code you received.";
        break;
      case "app_verification":
        instructions = "App Verification Required - Please check your LinkedIn app and complete the verification there.";
        break;
      default:
        instructions = "Verification Required - Please enter the verification code.";
    }

    // Add retry message if this is a retry
    if (isRetry) {
      instructions += "\n\nPrevious code was incorrect. Please try again.";
    }

    return instructions;
  };

  // Redirect if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Show loading while checking for existing sessions
  if (isCheckingSession) {
    return (
      <Layout width="1000px">
        <div className="row flex-grow-1 justify-content-center">
          <div className="col d-flex justify-content-center align-items-center">
            <div className="text-center">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="mt-3">Checking for existing sessions...</div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout width="1000px">
      <div className="row flex-grow-1 justify-content-center">
        <div className="col d-flex" style={{ position: "relative" }}>
          <img
            src="/birdie_logo.svg"
            alt="Company Logo"
            style={{ height: "40px", position: "absolute", top: "0" }}
          />
          <div>
            <div
              className="font-size-large font-weight-bold"
              style={{ marginTop: "120px" }}
            >
              LinkedIn Cloud Integration
            </div>
            <div>
              Connect your LinkedIn account to enable cloud features
            </div>
          </div>
        </div>
        <div className="col d-flex">
          <div className="flex-grow-1 d-flex flex-column justify-content-between">
            <div style={{ marginTop: "calc(120px - 0.5rem)" }}>
              {showApiKeySection && apiKey ? (
                // API Key Section
                <div>
                  <div className="alert alert-success mb-3">
                    <strong>LinkedIn Integration Successful!</strong><br />
                    Your LinkedIn account has been successfully connected. Here's your API key and usage instructions.
                  </div>

                  <div className="card mb-3">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">Your API Key</h6>
                      <div>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-warning me-2"
                          onClick={regenerateApiKey}
                          disabled={isLoading}
                          title="Regenerate API Key"
                        >
                          {isLoading ? (
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          ) : (
                            "🔄 Regenerate"
                          )}
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => copyToClipboard(apiKey)}
                          title="Copy API Key"
                        >
                          {copySuccess ? (
                            <span className="text-success">✓ Copied!</span>
                          ) : (
                            <span>📋 Copy</span>
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="card-body">
                      <code className="d-block p-2 bg-light rounded" style={{ fontSize: '0.9em', wordBreak: 'break-all' }}>
                        {apiKey}
                      </code>
                    </div>
                  </div>

                  <div className="card mb-3">
                    <div className="card-header">
                      <h6 className="mb-0">Test Your API</h6>
                    </div>
                    <div className="card-body">
                      <p className="card-text mb-3">
                        Use this curl command to test your API key:
                      </p>
                      <div className="position-relative">
                        <code className="d-block p-3 bg-dark text-light rounded" style={{ fontSize: '0.9em' }}>
                          curl -H "Authorization: Bearer {apiKey}" {REDIRECT_ROOT}/api/v0/profile/dhung09
                        </code>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-light position-absolute"
                          style={{ top: '0.5rem', right: '0.5rem' }}
                          onClick={() => copyToClipboard(`curl -H "Authorization: Bearer ${apiKey}" ${REDIRECT_ROOT}/api/v0/profile/dhung09`)}
                          title="Copy Command"
                        >
                          📋
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="card mb-3">
                    <div className="card-header">
                      <h6 className="mb-0">API Usage Instructions</h6>
                    </div>
                    <div className="card-body">
                      <ul className="mb-0">
                        <li>Include your API key in the <code>Authorization</code> header as <code>Bearer {apiKey.substring(0, 8)}...</code></li>
                        <li>The API base URL is <code>{REDIRECT_ROOT}/api/v0</code></li>
                        <li>Available endpoints include profile lookup, company data, and more</li>
                        <li>Keep your API key secure and don't share it publicly</li>
                      </ul>
                    </div>
                  </div>

                  {existingSessionId && (
                    <div className="card mb-3">
                      <div className="card-header">
                        <h6 className="mb-0">Session Management</h6>
                      </div>
                      <div className="card-body">
                        <p className="card-text mb-3">
                          <strong>Active Session:</strong> {existingSessionId}
                        </p>
                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-info"
                            onClick={testSession}
                            disabled={isLoading}
                          >
                            {isLoading ? (
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            ) : null}
                            Test Session
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={resetSession}
                            disabled={isLoading}
                          >
                            Reset Session
                          </button>
                        </div>
                        {sessionTestResult && (
                          <div className="mt-2">
                            <small className="text-muted">{sessionTestResult}</small>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={resetForm}
                  >
                    Start New Integration
                  </button>
                </div>
              ) : existingSessionId ? (
                // Existing Session Section
                <div>
                  <div className="alert alert-info mb-3">
                    <strong>Active Session Found</strong><br />
                    You already have an active LinkedIn session. Please use the existing session or contact support if you need to create a new one.
                  </div>
                  <div className="card mb-3">
                    <div className="card-body">
                      <h6 className="card-title">Existing Session Details</h6>
                      <p className="card-text">
                        <strong>Username:</strong> {username}<br />
                        <strong>Session ID:</strong> {existingSessionId}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={resetForm}
                  >
                    Try Different Account
                  </button>
                </div>
              ) : !showChallengeSection ? (
                // Login Form
                <form onSubmit={handleLogin}>
                  <input
                    type="text"
                    className={`form-control ${error ? "is-invalid" : ""}`}
                    placeholder="LinkedIn Username/Email"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                  <div className="mt-2">
                    <input
                      type="password"
                      className={`form-control ${error ? "is-invalid" : ""}`}
                      placeholder="LinkedIn Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isLoading}
                      required
                    />
                  </div>
                  <div className="mt-3">
                    <button
                      type="submit"
                      className="btn btn-primary me-2"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                      ) : null}
                      Start Login
                    </button>
                  </div>
                </form>
              ) : (
                // Challenge Section
                <div>
                  {currentChallengeAnalysis?.challenge_type === "app_verification" ? (
                    <div>
                      <div className="alert alert-info mb-3">
                        <strong>App Verification Required</strong><br />
                        Please check your LinkedIn app and complete the verification there.
                        {isPolling && (
                          <div className="mt-2">
                            <div className="d-flex align-items-center">
                              <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                              <span>Automatically checking verification status...</span>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleAppVerification}
                          disabled={isLoading || isPolling}
                        >
                          {isLoading ? (
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                          ) : null}
                          Check Now
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={resetForm}
                          disabled={isPolling}
                        >
                          Start Over
                        </button>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleChallengeSubmit}>
                      <div className="alert alert-info mb-3">
                        {currentChallengeAnalysis ? getChallengeInstructions(currentChallengeAnalysis, isRetry) : "Verification Required"}
                      </div>
                      <input
                        type="text"
                        className="form-control mb-3"
                        placeholder="Enter the verification code"
                        value={challengeCode}
                        onChange={(e) => setChallengeCode(e.target.value)}
                        disabled={isLoading}
                        required
                      />
                      <div className="d-flex gap-2">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                          ) : null}
                          Submit Code
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={resetForm}
                        >
                          Start Over
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}

              {/* Status Messages */}
              {error && (
                <div className="mt-3">
                  <div className="alert alert-danger py-2" role="alert">
                    {error}
                  </div>
                </div>
              )}

              {message && (
                <div className="mt-3">
                  <div className="alert alert-success py-2" role="alert">
                    {message}
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="mt-4 d-flex align-items-center justify-content-end">
                <a
                  href="/subscription"
                  className="text-decoration-none me-4"
                >
                  Back to Dashboard
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}